import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Fragment } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { checkIfStaffIsCurrentlyInJourney } from '../../../../api/staff/check-if-staff-is-currently-in-journey'
import { getStaffTotalJourneys } from '../../../../api/staff/get-staff-total-journeys'
import { Paths } from '../../../../lib/constants/path'
import useTranslate from '../../../../lib/hooks/use-translate'
import { languageEnumMapping } from '../../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../../lib/types/enums/languages/language-level.enum'
import { staffPositionEnumMapping } from '../../../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { getStaffDisplayName } from '../../../../lib/utils/functions/staff.functions'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import { constructCenterPath } from '../../../../views/centers/utils'
import { ProfileImageInput } from '../../../elements/inputs/profile-image-input'

const StaffContainerInformation = () => {
  const { t } = useTranslate()
  const form = useFormContext<AdminStaffInput>()
  const languageMapping = languageEnumMapping()

  const { id: idParam } = useParams<{ id: string }>()
  const staffId = idParam ? Number(idParam) : NaN
  const isValidStaffId = Number.isInteger(staffId)

  const { data: staffCurrentJourney, isPending: isStaffCurrentlyInJourneyPending } = useQuery({
    queryKey: ['staff-currently-in-journey', staffId],
    queryFn: () => checkIfStaffIsCurrentlyInJourney(staffId),
    enabled: isValidStaffId
  })

  const { data: staffTotalJourney, isPending: isStaffTotalJourneyPending } = useQuery({
    queryKey: ['staff-total-journeys', staffId],
    queryFn: () => getStaffTotalJourneys(staffId),
    enabled: isValidStaffId
  })

  const [
    firstName,
    lastName,
    maidenName,
    position,
    qualifications,
    picture,
    languages,
    birthDate,
    postalCode,
    city,
    phoneNumber,
    email
  ] = form.watch([
    'firstName',
    'lastName',
    'maidenName',
    'position',
    'qualifications',
    'picture',
    'languages',
    'birthDate',
    'postalCode',
    'city',
    'phoneNumber',
    'email'
  ])

  const getDisplayName = () => getStaffDisplayName(firstName, lastName, maidenName)
  const practicedLanguages = languages.filter(
    (language) => language.level && language.level !== LanguageLevelEnum.NOT_PRACTICED
  )

  const cityAndPostalCode = postalCode && city ? `${postalCode} , ${city}` : postalCode || city

  return (
    <InnerBox className="p-4 rounded-2xl">
      <div className="flex gap-4 w-full items-center">
        <ProfileImageInput
          title={getDisplayName()}
          file={picture}
          onFileChange={(value) => form.setValue('picture', value)}
        />
        <div className="w-full">
          <div className="flex gap-2 items-center">
            <Typography variant="text" color={tailwindColors.black.DEFAULT}>
              {getDisplayName()}
            </Typography>
            {isStaffCurrentlyInJourneyPending && isValidStaffId ? (
              <Loader center />
            ) : (
              staffCurrentJourney?.isInJourney && (
                <div className="border border-blue-light p-2 rounded flex gap-1 items-center">
                  <Typography variant="tag" color={tailwindColors.blue.DEFAULT}>
                    {staffCurrentJourney.centers.length > 0
                      ? 'En séjour actuellement -'
                      : 'En séjour actuellement'}
                  </Typography>
                  {staffCurrentJourney.centers.map((center, index) => (
                    <Fragment key={center.id}>
                      <TextLink
                        name={center.name}
                        href={constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id)}
                      />
                      {index < staffCurrentJourney.centers.length - 1 && ', '}
                    </Fragment>
                  ))}
                </div>
              )
            )}
          </div>
          {(city || postalCode) && (
            <Typography
              variant="label-semibold"
              color={tailwindColors.grey.DEFAULT}
              className="py-1"
            >
              {cityAndPostalCode}
            </Typography>
          )}
          {birthDate && (
            <Typography
              variant="label-semibold"
              color={tailwindColors.grey.DEFAULT}
              className="py-1"
            >
              {`${DateFormatter.DDMMYYYY(birthDate)} - (Âge : ${DateFormatter.calculateAge(birthDate)} ans)`}
            </Typography>
          )}
          {(phoneNumber || email) && (
            <Typography
              variant="label-semibold"
              color={tailwindColors.grey.DEFAULT}
              className="py-1"
            >
              {phoneNumber && formatPhoneNumber(phoneNumber)}
              {phoneNumber && email && ' - '}
              {email && <TextLink href={`mailto:${email}`} name={email} />}
            </Typography>
          )}
          <div className="w-full flex justify-between items-center mt-2 flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap">
              {position && <Badge variant="grey" text={t(staffPositionEnumMapping[position])} />}
              {qualifications && qualifications.length > 0 && (
                <Badge
                  size="flexible"
                  variant={'grey'}
                  text={qualifications
                    .map((qualification) => t(staffQualificationEnumMapping[qualification].label))
                    .join(', ')}
                />
              )}
              {practicedLanguages.length > 0 && (
                <Badge
                  variant="grey"
                  text="Langues parlées"
                  icon={
                    <div className="flex gap-1">
                      {practicedLanguages.map((language) => {
                        const Icon = languageMapping[language.language]?.icon
                        return (
                          <div key={language.language}>
                            {<Icon className={'w-5 h-5 rounded-full'} />}
                          </div>
                        )
                      })}
                    </div>
                  }
                />
              )}
            </div>
            {!isStaffTotalJourneyPending && isValidStaffId && (
              <Badge
                variant="grey"
                text={staffTotalJourney ? `Nombre de séjours : ${staffTotalJourney}` : '-'}
              />
            )}
          </div>
        </div>
      </div>
    </InnerBox>
  )
}

export default StaffContainerInformation
